import {
  ArrowRight,
  Building2,
} from "lucide-react";

import type {
  PilotRequest,
  PilotRequestStatus,
} from "@/api/platform";

interface PendingRequestsCardProps {
  requests: PilotRequest[];
}

const pendingStatuses = new Set<PilotRequestStatus>([
  "SUBMITTED",
  "UNDER_REVIEW",
]);

const statusClasses: Partial<
  Record<PilotRequestStatus, string>
> = {
  SUBMITTED:
    "bg-indigo-50 text-indigo-700",
  UNDER_REVIEW:
    "bg-amber-50 text-amber-700",
};

const statusLabels: Partial<
  Record<PilotRequestStatus, string>
> = {
  SUBMITTED: "Submitted",
  UNDER_REVIEW: "Under Review",
};

export function PendingRequestsCard({
  requests,
}: PendingRequestsCardProps) {
  const pendingRequests = requests.filter(
    (request) =>
      pendingStatuses.has(request.status)
  );

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-950">
            Pending requests
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            Churches waiting for pilot review.
          </p>
        </div>

        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
          <Building2 className="h-5 w-5" />
        </div>
      </div>

      {pendingRequests.length > 0 ? (
        <div className="divide-y divide-slate-100">
          {pendingRequests.map((request) => (
            <PendingRequestItem
              key={request.id}
              request={request}
            />
          ))}
        </div>
      ) : (
        <div className="px-6 py-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
            <Building2 className="h-6 w-6" />
          </div>

          <p className="mt-4 text-sm font-semibold text-slate-900">
            No pending requests
          </p>

          <p className="mt-1 text-sm text-slate-500">
            New pilot access requests will appear here.
          </p>
        </div>
      )}

      <div className="border-t border-slate-100 p-5">
        <button
          type="button"
          className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 transition hover:text-indigo-500"
        >
          View all pilot requests
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </section>
  );
}

interface PendingRequestItemProps {
  request: PilotRequest;
}

function PendingRequestItem({
  request,
}: PendingRequestItemProps) {
  const statusClass =
    statusClasses[request.status] ??
    "bg-slate-100 text-slate-700";

  const statusLabel =
    statusLabels[request.status] ??
    formatStatus(request.status);

  return (
    <article className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-sm font-semibold text-slate-900">
            {request.church_name}
          </p>

          <span
            className={[
              "rounded-full px-2.5 py-1 text-xs font-semibold",
              statusClass,
            ].join(" ")}
          >
            {statusLabel}
          </span>
        </div>

        <p className="mt-2 text-sm text-slate-500">
          {request.contact_name}
          {request.church_size
            ? ` · ${request.church_size} members`
            : ""}
        </p>

        <p className="mt-1 text-xs font-medium text-slate-400">
          Requested{" "}
          {formatRelativeDate(
            request.submitted_at ??
              request.created_at
          )}
        </p>
      </div>

      <button
        type="button"
        className="inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-indigo-600 transition hover:text-indigo-500"
      >
        Review
        <ArrowRight className="h-4 w-4" />
      </button>
    </article>
  );
}

function formatStatus(value: string): string {
  return value
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map(
      (part) =>
        part.charAt(0).toUpperCase() +
        part.slice(1)
    )
    .join(" ");
}

function formatRelativeDate(
  value: string
): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "at an unknown time";
  }

  const elapsedMilliseconds =
    Date.now() - date.getTime();

  const elapsedMinutes = Math.floor(
    elapsedMilliseconds / 60_000
  );

  if (elapsedMinutes < 1) {
    return "just now";
  }

  if (elapsedMinutes < 60) {
    return `${elapsedMinutes} ${
      elapsedMinutes === 1
        ? "minute"
        : "minutes"
    } ago`;
  }

  const elapsedHours = Math.floor(
    elapsedMinutes / 60
  );

  if (elapsedHours < 24) {
    return `${elapsedHours} ${
      elapsedHours === 1 ? "hour" : "hours"
    } ago`;
  }

  const elapsedDays = Math.floor(
    elapsedHours / 24
  );

  if (elapsedDays === 1) {
    return "yesterday";
  }

  if (elapsedDays < 7) {
    return `${elapsedDays} days ago`;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year:
      date.getFullYear() !==
      new Date().getFullYear()
        ? "numeric"
        : undefined,
  }).format(date);
}